import React from "react";

import { Provider as JotaiStateProvider } from "jotai";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";

import { ThemeProvider } from "contexts/ThemeContext";

import { AppErrorBoundary } from "AppErrorBoundary";

import App from "./App";
import reportWebVitals from "./reportWebVitals";
import setupClarity from "./setupClarity";

import "./index.css";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PostHogErrorBoundary, PostHogProvider } from "@posthog/react";
import { setupGlobalErrorHandlers } from "utils/errorHandler";

posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY as string, {
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  defaults: "2026-01-30",
  autocapture: {
    dom_event_allowlist: ["click", "change", "submit"],
    url_allowlist: [window.location.origin],
    element_allowlist: ["a", "button", "form", "input", "select", "textarea"],
    css_selector_allowlist: ["[data-ph-capture]"],
  },
  capture_pageview: true,
  capture_pageleave: true,
  session_recording: {
    recordCrossOriginIframes: false,
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
  },
  enable_recording_console_log: true,
  disable_session_recording: false,
  loaded: (posthog) => {
    if (process.env.REACT_APP_ENV === "local") {
      posthog.debug();
    }
    posthog.register({
      environment: process.env.NODE_ENV,
      app_env: process.env.REACT_APP_ENV,
    });
  },
  capture_performance: true,
  persistence: "localStorage+cookie",
  property_blacklist: [],
});

setupGlobalErrorHandlers();

Sentry.init({
  dsn: "https://6ac700a1be2cdd563c31742da1e3793c@o4511139301163008.ingest.us.sentry.io/4511139304898560",
  environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/.*\.preimage\.io/,
    /^https:\/\/api\.preimage\.io/,
  ],
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
});

const root = createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary>
        <AppErrorBoundary>
          <HelmetProvider>
            <ThemeProvider>
              <JotaiStateProvider>
                <App />
              </JotaiStateProvider>
            </ThemeProvider>
          </HelmetProvider>
        </AppErrorBoundary>
      </PostHogErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.debug))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
if (
  process.env.REACT_APP_ENV === "local" &&
  process.env.REACT_APP_ENABLE_WEB_VITALS
) {
  reportWebVitals(console.debug);
}

if (process.env.REACT_APP_ENV === "prod") {
  // Start Clarity metrics collection only in PROD env
  setupClarity();
}
